import React from "react";
import { Calendar, Clock } from "lucide-react";
import {
  Pagination,
  SimpleTooltip,
  TabSelector,
} from "../../common";
import { DataTable } from "../../common/Table2";
import type { TableColumn } from "../../common/Table";
import Badge from "../../common/Badge";
import { getWorkflowStatusVariant } from "../../../utils/badgeVariants";
import { getWorkflowStatusFallbackLabel } from "../../../utils/constants";
import type { GameBookingItem } from "../../../store/apis/gameBooking.api";
import { capitalizeWords } from "../../../utils/nameUtils";
import { TimesheetManagementSkeleton } from "../../timesheet/Skeleton";
import { HEADER_CLS } from "../../../utils/constants";
import { COMP_OFF_STATUS_FILTER_OPTIONS } from "../../../utils/compOff";
import { formatTimeOnly, formatDateDDMMMYYYY, getUserTimezone } from "../../../utils/timeUtils";

export type BookingStatusFilter = "all" | "pending" | "approved" | "rejected";

interface GameBookingTableProps {
  title: string;
  bookings: GameBookingItem[];
  loading: boolean;
  onViewDetails: (booking: GameBookingItem) => void;
  onCancel?: (booking: GameBookingItem) => void;
  canCancel?: (booking: GameBookingItem) => boolean;
  statusFilter?: BookingStatusFilter;
  onStatusFilterChange?: (value: BookingStatusFilter) => void;
  pagination?: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (size: number) => void;
  };
}

const GameBookingTable: React.FC<GameBookingTableProps> = ({
  title,
  bookings,
  loading,
  statusFilter,
  onStatusFilterChange,
  pagination,
}) => {
  const userTimezone = getUserTimezone();

  const columns: TableColumn<GameBookingItem>[] = [
    {
      key: "gameName",
      title: "Game",
      label: "Game",
      required: true,
      headerClassName: HEADER_CLS,
      render: (_, record) => (
        <span className="text-label font-bold text-secondary">
          {record.gameName}
        </span>
      ),
    },
    {
      key: "date",
      title: "Date",
      label: "Date",
      headerClassName: HEADER_CLS,
      render: (_, record) => (
        <span className="text-sm text-[rgb(91,100,114)]">
          {formatDateDDMMMYYYY(record.startTime, userTimezone)}
        </span>
      ),
    },
    {
      key: "timeSlot",
      title: "Time Slot",
      label: "Time Slot",
      headerClassName: HEADER_CLS,
      render: (_, record) => (
        <div className="flex items-center gap-1">
          <span className="text-label text-secondary">
            {formatTimeOnly(record.startTime, userTimezone)} - {formatTimeOnly(record.endTime, userTimezone)}
          </span>
        </div>
      ),
    },
    {
      key: "duration",
      title: "Duration",
      label: "Duration",
      headerClassName: HEADER_CLS,
      render: (_, record) => (
        <span className="text-label text-secondary">{record.duration} min</span>
      ),
    },
    {
      key: "creatorName",
      title: "Created By",
      label: "Created By",
      headerClassName: HEADER_CLS,
      render: (_, record) => (
        <span className="text-label text-secondary capitalize">
          {record.creatorName || "—"}
        </span>
      ),
    },
    {
      key: "participants",
      title: "Participants",
      label: "Participants",
      headerClassName: HEADER_CLS,
      render: (_, record) => (
        <SimpleTooltip
          label={(record.participantNames ?? []).join(", ") || "—"}
          side="top"
        >
          <span className="text-label text-secondary">
            {(record.participantNames ?? []).length} participant(s)
          </span>
        </SimpleTooltip>
      ),
    },
    {
      key: "status",
      title: "Status",
      label: "Status",
      headerClassName: HEADER_CLS,
      render: (_, record) => (
        <Badge variant={getWorkflowStatusVariant(record.status)} size="middle">
          {record.statusLabel ??
            capitalizeWords(
              getWorkflowStatusFallbackLabel(record.status, record.currentActorName) ?? record.status,
            )}
        </Badge>
      ),
    },
  ];

  return (
    <div className="rounded-2xl bg-surface border border-border-primary shadow-soft overflow-hidden mt-4">
      <div className="px-6 py-4 border-b border-border-primary flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <h3 className="text-base font-extrabold tracking-tight text-text-primary leading-tight">{title}</h3>
        {statusFilter && onStatusFilterChange && (
          <TabSelector
            tabs={COMP_OFF_STATUS_FILTER_OPTIONS.map(opt => ({ k: opt.value, label: opt.label }))}
            active={statusFilter}
            onChange={(val) => onStatusFilterChange(val as any)}
            variant="segmented"
            size="sm"
          />
        )}
      </div>
      <DataTable<GameBookingItem>
        columns={columns}
        rows={bookings}
        loading={loading}
        skeleton={<TimesheetManagementSkeleton rows={5} />}
        emptyMessage="No bookings found"
        rowKey={(b) => b.id}
      />

      {pagination && (
        <div className="p-6 pt-0 mt-4">
          <Pagination
            currentPage={pagination.currentPage}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.itemsPerPage}
            onPageChange={pagination.onPageChange}
            onItemsPerPageChange={pagination.onItemsPerPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default GameBookingTable;
